import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { MerchantSettingsContext } from './use-merchant-settings';

export type MerchantSettingsPane = 'limits' | 'addresses' | 'auto';

export interface MerchantSettingsRequest {
  merchantId: string;
  email?: string;
  pane: MerchantSettingsPane;
  highlight: string;
  fromPicker: boolean;
}

interface OpenOptions {
  email?: string;
  pane?: MerchantSettingsPane;
  highlight?: string;
}

export function MerchantSettingsProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<MerchantSettingsRequest | 'picker' | null>(null);
  const openMerchantSettings = useCallback((merchantId: string | number, options?: OpenOptions) => {
    setRequest({
      merchantId: String(merchantId),
      email: options?.email,
      pane: options?.pane ?? 'limits',
      highlight: options?.highlight ?? '',
      fromPicker: false,
    });
  }, []);
  const openMerchantPicker = useCallback(() => setRequest('picker'), []);
  const closeMerchantSettings = useCallback(() => setRequest(null), []);
  const selectWaitingMerchant = useCallback((merchantId: string | number, email?: string) => {
    setRequest({
      merchantId: String(merchantId),
      email,
      pane: 'auto',
      highlight: '',
      fromPicker: true,
    });
  }, []);
  const value = useMemo(
    () => ({ request, openMerchantSettings, openMerchantPicker, closeMerchantSettings, selectWaitingMerchant }),
    [closeMerchantSettings, openMerchantPicker, openMerchantSettings, request, selectWaitingMerchant],
  );
  return <MerchantSettingsContext.Provider value={value}>{children}</MerchantSettingsContext.Provider>;
}
